import { useEffect, useState } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { UserButton } from "@clerk/react";
import { Menu, Moon, Plus, Sun } from "lucide-react";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import { Select, SelectContent, SelectItem, SelectSeparator, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Sidebar } from "@/components/layout/Sidebar";
import { DemoBadge } from "@/components/common/States";
import { useWorkspace } from "@/context/WorkspaceContext";
import { CreditBanner, CreditChip, useWallet } from "@/components/billing/Credit";
import { useTheme } from "@/context/ThemeContext";

const NEW_ORG = "__new__";

function OrgSwitcher() {
  const { memberships, orgId, switchOrg } = useWorkspace();
  const navigate = useNavigate();
  return (
    <Select value={orgId || ""} onValueChange={(v) => (v === NEW_ORG ? navigate("/onboarding") : (switchOrg(v), navigate("/")))}>
      <SelectTrigger className="h-9 w-[180px] sm:w-[260px]" data-testid="org-switcher"><SelectValue placeholder="Select organisation" /></SelectTrigger>
      <SelectContent>
        {memberships.map((m) => (
          <SelectItem key={m.organisation_id} value={m.organisation_id} data-testid={`org-option-${m.organisation_id}`}>
            {m.organisation.name}{m.organisation.is_demo ? " · DEMO" : ""}
          </SelectItem>
        ))}
        <SelectSeparator />
        <SelectItem value={NEW_ORG} data-testid="org-option-create"><span className="inline-flex items-center gap-1.5"><Plus className="h-3.5 w-3.5" />Create organisation</span></SelectItem>
      </SelectContent>
    </Select>
  );
}

function Topbar({ onMenu, wallet }) {
  const { org, roleLabel } = useWorkspace();
  const { theme, toggle } = useTheme();
  return (
    <header className="sticky top-0 z-30 flex h-16 items-center gap-3 border-b bg-background/85 px-4 backdrop-blur-xl md:px-8">
      <Button variant="ghost" size="icon" className="lg:hidden" onClick={onMenu} data-testid="mobile-menu-button"><Menu className="h-5 w-5" /></Button>
      <OrgSwitcher />
      {org?.is_demo && <span className="hidden sm:inline-flex"><DemoBadge /></span>}
      <div className="flex-1" />
      <CreditChip wallet={wallet} />
      {roleLabel && <span className="hidden text-xs text-muted-foreground md:inline" data-testid="current-role">{roleLabel}</span>}
      <Button variant="ghost" size="icon" onClick={toggle} data-testid="theme-toggle" aria-label="Toggle theme">
        {theme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
      </Button>
      <div data-testid="user-menu"><UserButton /></div>
    </header>
  );
}

export default function AppShell() {
  const [open, setOpen] = useState(false);
  const { orgId, org } = useWorkspace();
  const { data: wallet, reload } = useWallet();
  const { pathname } = useLocation();
  useEffect(() => { reload(); }, [pathname, reload]);
  return (
    <div className="min-h-screen bg-background">
      <aside className="fixed inset-y-0 left-0 hidden w-64 lg:block"><Sidebar /></aside>
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="left" className="w-64 border-0 p-0"><SheetTitle className="sr-only">Navigation</SheetTitle><Sidebar onNavigate={() => setOpen(false)} /></SheetContent>
      </Sheet>
      <div className="lg:pl-64">
        <Topbar onMenu={() => setOpen(true)} wallet={wallet} />
        <CreditBanner wallet={wallet} />
        {org?.is_demo && <div className="border-b border-amber-500/30 bg-amber-500/10 px-4 py-2 text-xs text-amber-700 dark:text-amber-300 sm:hidden" data-testid="demo-banner-mobile">Demo workspace — all records are synthetic.</div>}
        <main key={orgId} className="max-w-[1500px] p-4 md:p-8"><Outlet /></main>
      </div>
    </div>
  );
}
